import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Widget, WidgetProvider } from '@/components/widget';
import { Loader2, AlertCircle } from 'lucide-react';
import type { PricingConfig, WidgetConfig, BrandingConfig, SherwinWilliamsColor } from '@/types/firestore';
import { getDefaultPricingConfig } from '@/types/firestore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Convert HEX color to HSL format for Tailwind CSS variables
 * Tailwind expects HSL values like "213 94% 50%" (without hsl() wrapper)
 *
 * @param hex - Color in hex format (e.g., "#2563eb" or "2563eb")
 * @returns HSL string in format "H S% L%" or null if invalid
 */
function hexToHsl(hex: string): string | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    console.warn('[Widget] Invalid hex color:', hex);
    return null;
  }

  // Parse hex to RGB
  const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
  const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
  const b = parseInt(cleanHex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Return HSL in Tailwind format: "H S% L%"
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

interface TenantPublicConfig {
  tenant_id: string;
  company_name: string;
  branding: BrandingConfig;
  widget: WidgetConfig;
  pricing: PricingConfig;
  custom_colors?: SherwinWilliamsColor[];
}

export default function WidgetPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [config, setConfig] = useState<TenantPublicConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // tenantId from URL is actually the slug (e.g., "debug-renovations")
  const slug = tenantId;

  useEffect(() => {
    const loadTenantConfig = async () => {
      if (!slug) {
        setError('Invalid widget configuration');
        setIsLoading(false);
        return;
      }

      // DEV MODE: Use mock config for quick testing
      if (import.meta.env.DEV && slug === 'demo') {
        console.log('[Widget] DEV MODE - Using mock config for demo tenant');
        setConfig({
          tenant_id: 'demo',
          company_name: 'Demo Renovations',
          branding: {},
          widget: {
            enabled: true,
            watermark_text: 'RenovationAI',
            show_quote: true,
            require_address: true,
            require_timeline: true,
          },
          pricing: getDefaultPricingConfig(),
        });
        setIsLoading(false);
        return;
      }

      try {
        console.log('[Widget] Loading config for tenant slug:', slug);

        // Fetch from PostgreSQL API
        const response = await fetch(`${API_BASE_URL}/widget/${slug}`);
        const result = await response.json();

        if (!response.ok) {
          console.error('[Widget] API error:', result.error);
          setError(result.error || 'Widget not found');
          setIsLoading(false);
          return;
        }

        console.log('[Widget] API response:', result.data);
        setConfig(result.data);
      } catch (err) {
        console.error('[Widget] Error loading tenant config:', err);
        setError('Unable to load widget');
      } finally {
        setIsLoading(false);
      }
    };

    loadTenantConfig();
  }, [slug]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !config || !slug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <h1 className="text-lg font-semibold">Widget Unavailable</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {error || 'This renovation widget is not available.'}
          </p>
        </div>
      </div>
    );
  }

  // Apply tenant branding via CSS custom properties
  // Convert HEX colors to HSL format for Tailwind compatibility
  const brandingStyle: React.CSSProperties = {};
  if (config.branding.primary_color) {
    const hsl = hexToHsl(config.branding.primary_color);
    if (hsl) {
      brandingStyle['--primary'] = hsl;
      brandingStyle['--accent'] = hsl;
    }
  }
  if (config.branding.secondary_color) {
    const hsl = hexToHsl(config.branding.secondary_color);
    if (hsl) {
      brandingStyle['--secondary'] = hsl;
    }
  }
  if (config.branding.background_color) {
    const hsl = hexToHsl(config.branding.background_color);
    if (hsl) {
      brandingStyle['--background'] = hsl;
    }
  }
  if (config.branding.button_text_color) {
    const hsl = hexToHsl(config.branding.button_text_color);
    if (hsl) {
      brandingStyle['--primary-foreground'] = hsl;
      brandingStyle['--accent-foreground'] = hsl;
    }
  }

  const hasCustomBranding = Object.keys(brandingStyle).length > 0;

  return (
    <div style={hasCustomBranding ? brandingStyle : undefined}>
      <WidgetProvider tenantId={config.tenant_id} tenantConfig={config}>
        <Widget />
      </WidgetProvider>
    </div>
  );
}
